/**
 * The responsibility of the custom hook to manage continuous day event form logic.
 *
 * @function useContinuousDayEvent
 * @returns {Object} An object containing the Formik instance for managing the continuous day event form
 *
 * @description
 * This hook handles the form state and validation for continuous day events. It performs the following steps:
 * 1. Sets up Redux state access for user and event data
 * 2. Initializes helper function to detect changes in event data
 * 3. Sets up initial form values from Redux state
 * 4. Configures Formik with:
 *    - Form validation schema using Yup
 *    - Custom validation rules for date/time logic
 *    - Form submission handler that:
 *      a. Formats date/time values
 *      b. Only dispatches update if values have changed
 *      c. Advances to next tab upon completion
 *      d. Includes error handling for submission failures
 *
 * The validation ensures that:
 * - Dates are not in the past
 * - End date is after start date
 * - End time is after start time on the same day
 * - Start and end dates are different for continuous events
 */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { updateEventById, nextTab } from '../../../../../../app/store/slices/event/event';
import { errorToast } from '../../../../../../utils/toasts/toasts';

export default function useContinuousDayEvent() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { eventData } = useSelector((state) => state.event);

    const [loading, setLoading] = useState(false);

    function hasContinuousDayEventChanged(values, eventData) {
        const cleanedValues = {
            start_date: dayjs(values.startDate).format('YYYY-MM-DD'),
            end_date: dayjs(values.endDate).format('YYYY-MM-DD'),
            start_time: dayjs(values.startTime, 'HH:mm').format('HH:mm:ss'),
            end_time: dayjs(values.endTime, 'HH:mm').format('HH:mm:ss')
        };

        const originalData = {
            start_date: dayjs(eventData?.start_date).format('YYYY-MM-DD'),
            end_date: dayjs(eventData?.end_date).format('YYYY-MM-DD'),
            start_time: dayjs(eventData?.start_time, 'HH:mm').format('HH:mm:ss'),
            end_time: dayjs(eventData?.end_time, 'HH:mm').format('HH:mm:ss')
        };

        return !isEqual(cleanedValues, originalData);
    }

    const initialValues = {
        startDate:
            eventData?.event_type === 'continuous' && eventData?.start_date
                ? dayjs(eventData.start_date)
                : null,
        endDate:
            eventData?.event_type === 'continuous' && eventData?.end_date
                ? dayjs(eventData.end_date)
                : null,
        startTime:
            eventData?.event_type === 'continuous' && eventData?.start_time
                ? dayjs(eventData.start_time, 'HH:mm')
                : null,
        endTime:
            eventData?.event_type === 'continuous' && eventData?.end_time
                ? dayjs(eventData.end_time, 'HH:mm')
                : null
    };

    const continuosFormik = useFormik({
        initialValues,
        validationSchema,
        validate: (values) => {
            const errors = {};

            // start date should not be in the past
            if (dayjs(values.startDate, 'DD/MM/YYYY').isBefore(dayjs(), 'day')) {
                errors.startDate = 'Start date cannot be in the past';
            }

            // start time should not be in the past if date is same as today
            if (
                dayjs(values.startDate, 'DD/MM/YYYY').isSame(dayjs(), 'day') &&
                dayjs(values.startTime, 'HH:mm').isBefore(dayjs(), 'minute')
            ) {
                errors.startTime = 'Start time cannot be in the past';
            }

            // end date should not be before start date
            if (
                dayjs(values.endDate, 'DD/MM/YYYY').isBefore(dayjs(values.startDate, 'DD/MM/YYYY'))
            ) {
                errors.endDate = 'End date cannot be before start date';
            }

            // end time should not be before start time if date is same
            if (
                dayjs(values.endDate, 'DD/MM/YYYY').isSame(dayjs(values.startDate, 'DD/MM/YYYY')) &&
                dayjs(values.endTime, 'HH:mm').isBefore(dayjs(values.startTime, 'HH:mm'))
            ) {
                errors.endTime = 'End time cannot be before start time on the same day';
            }

            // end date and start date should not be same in continuous day event
            if (dayjs(values.startDate, 'DD/MM/YYYY').isSame(dayjs(values.endDate, 'DD/MM/YYYY'))) {
                errors.endDate = 'End date cannot be same as start date in continuous day event';
            }

            return errors;
        },
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const payload = {
                    step: 'schedule',
                    user_id: user.id,
                    event_type: 'continuous',
                    start_date: dayjs(values.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    end_date: dayjs(values.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                    start_time: dayjs(values.startTime, 'HH:mm').format('HH:mm'),
                    end_time: dayjs(values.endTime, 'HH:mm').format('HH:mm')
                };

                // console.log("Continuous Day Event Form Submitted", payload);

                if (hasContinuousDayEventChanged(values, eventData)) {
                    await dispatch(
                        updateEventById({
                            eventId: eventData?.eventId,
                            eventData: payload
                        })
                    ).unwrap();
                }
                dispatch(nextTab());
            } catch (error) {
                errorToast(
                    error?.message || 'Failed to update continuous day event schedule'
                );
            } finally {
                setLoading(false);
            }
        }
    });

    return {
        continuosFormik,
        loading
    };
}

const validationSchema = Yup.object({
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required'),
    startTime: Yup.string().required('Start time is required'),
    endTime: Yup.string().required('End time is required')
});
